import React, { useEffect, useState, useRef } from "react";
import { Lock, Loader2, Maximize2, X, ChevronLeft, ChevronRight, Play, RefreshCw } from "lucide-react";
import axios from "axios";
import api, { mediaUrl, API } from "../lib/api";
import { b642buf, isChunkedMedia } from "../lib/crypto";
import { getAvatarIv } from "../lib/identity";
import { useCrypto } from "../context/CryptoContext";
import { ZoomableImage } from "./ZoomableImage";

// Text-overlay rendering (words layered on top of media, NOT burned in).
// Mirrors the geometry model used by PostDesigner so compose preview ≈ feed.
const OV_FONTS = {
  display: { family: "'Cabinet Grotesk', sans-serif", weight: 800 },
  text: { family: "'Satoshi', sans-serif", weight: 500 },
};
const OV_SCRIMS = {
  none: "none",
  bottom: "linear-gradient(to top, rgba(0,0,0,0.62), rgba(0,0,0,0) 55%)",
  top: "linear-gradient(to bottom, rgba(0,0,0,0.62), rgba(0,0,0,0) 55%)",
  even: "rgba(0,0,0,0.32)",
};

const TextOverlay = ({ overlay }) => {
  const ref = useRef(null);
  const [h, setH] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const measure = () => setH(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!overlay || !Array.isArray(overlay.boxes) || !overlay.boxes.length) return null;
  const scrimCss = OV_SCRIMS[overlay.scrim] || "none";
  return (
    <div ref={ref} className="absolute inset-0 pointer-events-none z-20 overflow-hidden" data-testid="media-text-overlay">
      {scrimCss !== "none" && <div className="absolute inset-0" style={{ background: scrimCss }} />}
      {overlay.boxes.map((b, i) => {
        const f = OV_FONTS[b.font] || OV_FONTS.display;
        return (
          <div key={i} style={{
            position: "absolute", left: `${b.xPct * 100}%`, top: `${b.yPct * 100}%`,
            width: `${b.widthPct * 100}%`, transform: "translate(-50%, -50%)",
            textAlign: b.align, fontFamily: f.family, fontWeight: f.weight, color: b.color,
            fontSize: h ? `${b.sizePct * h}px` : "20px", lineHeight: 1.2,
            whiteSpace: "pre-wrap", wordBreak: "break-word",
            textShadow: b.shadow ? "0 2px 8px rgba(0,0,0,0.55)" : "none",
          }} data-testid={`overlay-box-${i}`}>{b.text}</div>
        );
      })}
    </div>
  );
};

export const Media = ({ path, type, className }) => {
  if (!path) return null;
  const url = mediaUrl(path);
  if (type === "video") {
    return (
      <video src={url} className={className} controls controlsList="nodownload" disableRemotePlayback playsInline data-testid="post-media-video" />
    );
  }
  return <img src={url} alt="" className={className} data-testid="post-media-image" />;
};

// At most a few full downloads at once, so a media-heavy feed never stalls a phone.
const MAX_FETCHES = 3;
let _active = 0;
const _waiting = [];
const limited = async (fn) => {
  if (_active >= MAX_FETCHES) await new Promise((r) => _waiting.push(r));
  _active++;
  try { return await fn(); } finally { _active--; const next = _waiting.shift(); if (next) next(); }
};

// Recently decrypted images stay ready, so the viewer and swiping open instantly.
const _imgCache = new Map();
const cacheTouch = (p) => { const u = _imgCache.get(p); _imgCache.delete(p); _imgCache.set(p, u); return u; };
const cachePut = (p, u) => {
  _imgCache.set(p, u);
  while (_imgCache.size > 40) { const [k, v] = _imgCache.entries().next().value; _imgCache.delete(k); URL.revokeObjectURL(v); }
};

// Decrypts E2E media to a blob URL; falls back to plain Media for legacy (unencrypted) posts.
export const SecureMedia = ({ path, iv, enc, type, className, contain, lazy, onExpand, fullscreen, onZoomChange, thumb, thumbIv, dmWith, preUrl, overlay, overlayCt, overlayIv, imgStyle }) => {
  const { decryptNetBytes, decryptDmBytes, decryptNetChunkedToBlob, decryptDmChunkedToBlob, decryptNetText, netReady, status } = useCrypto();
  const ready = dmWith ? (status === "unlocked") : netReady;
  const decryptBytes = dmWith ? ((buf, ivv) => decryptDmBytes(dmWith, buf, ivv)) : decryptNetBytes;
  const decryptChunked = dmWith ? ((buf, mime) => decryptDmChunkedToBlob(dmWith, buf, mime)) : decryptNetChunkedToBlob;
  const [blobUrl, setBlobUrl] = useState(null);
  const [thumbUrl, setThumbUrl] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | locked
  const [imgLoaded, setImgLoaded] = useState(false); // drives the fade-in once the sharp image paints
  const [inView, setInView] = useState(!contain && !lazy); // feed media loads only when scrolled near
  const [retry, setRetry] = useState(0);
  const [playRequested, setPlayRequested] = useState(false); // videos decrypt only on tap (avoids memory crashes)
  const videoRef = useRef(null);
  const [ov, setOv] = useState(null); // decrypted text-overlay metadata (posts only)

  // Resolve text overlay: plaintext posts carry it directly; encrypted posts
  // store it as ciphertext under the network key. DMs never have overlays.
  useEffect(() => {
    if (dmWith) { setOv(null); return; }
    if (overlay) { setOv(overlay); return; }
    if (!overlayCt) { setOv(null); return; }
    if (!enc || !netReady) return;
    let alive = true;
    (async () => {
      try {
        const txt = await decryptNetText(overlayCt, overlayIv);
        if (alive && txt) setOv(JSON.parse(txt));
      } catch { /* overlay is optional, ignore */ }
    })();
    return () => { alive = false; };
  }, [overlay, overlayCt, overlayIv, enc, netReady, dmWith, decryptNetText]);

  // Once a tapped video has decrypted, start it immediately so the viewer never
  // sees a frozen grey frame. Try with sound first; if the browser blocks that
  // (iOS autoplay policy), fall back to muted playback so it still moves.
  useEffect(() => {
    if (type !== "video" || !playRequested) return;
    const src = enc ? blobUrl : mediaUrl(path);
    if (!src) return;
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => { try { v.muted = true; v.play().catch(() => {}); } catch {} });
    /* eslint-disable-next-line */
  }, [blobUrl, playRequested, state]);

  // Decrypt the tiny blur-up thumbnail as soon as the network key is ready, it's
  // inlined with the post so it paints instantly, before the full media downloads.
  useEffect(() => {
    if (!enc || !thumb || !ready) return;
    let revoked;
    (async () => {
      try {
        const plain = await decryptBytes(b642buf(thumb), thumbIv);
        if (plain) { const u = URL.createObjectURL(new Blob([plain], { type: "image/jpeg" })); revoked = u; setThumbUrl(u); }
      } catch { /* no placeholder */ }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [thumb, thumbIv, enc, ready, decryptBytes]);

  // Lazy-load: start fetching/decrypting only when near the viewport.
  const observe = (node) => {
    if (!node || inView) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { setInView(true); io.disconnect(); }
    }, { rootMargin: "300px" });
    io.observe(node);
  };

  useEffect(() => {
    let revoked;
    (async () => {
      if (preUrl) { setState("ok"); return; } // already-decrypted blob passed in, skip re-download
      if (!enc) { setState("ok"); return; }
      if (type !== "video" && _imgCache.has(path)) { setBlobUrl(cacheTouch(path)); setState("ok"); return; }
      if ((contain || lazy) && !inView) return;
      // Feed videos are heavy: don't download/decrypt until the member taps play.
      if (type === "video" && (contain || lazy) && !fullscreen && !playRequested) return;
      // Wait for the network key to finish unwrapping (first-login race), stay in
      // 'loading' while crypto is still initializing; only show 'locked' once crypto
      // is fully unlocked yet no network key exists (member not yet granted the key).
      if (!ready) { setState(status === "unlocked" ? "locked" : "loading"); return; }
      try {
        // Encrypted blobs are served publicly-cacheable, fetch WITHOUT the auth
        // header so the request hits the browser/edge cache (a per-user token
        // would bust caching). Ciphertext is safe: useless without the key.
        setState("loading");
        const mime = type === "video" ? "video/mp4" : "image/jpeg";
        const blob = await limited(async () => {
          const res = await axios.get(`${API}/files/${path}`, { responseType: "arraybuffer", timeout: type === "video" ? 0 : 90000 });
          // Large video framed into self-describing chunks, decrypt chunk-by-chunk.
          if (isChunkedMedia(res.data)) return decryptChunked(res.data, mime);
          const plain = await decryptBytes(res.data, iv);
          return plain ? new Blob([plain], { type: mime }) : null;
        }).catch((e) => (e && e.name === "OperationError" ? null : "error"));
        if (blob === "error") { setState("error"); return; }
        if (!blob) { setState("locked"); return; }
        const u = URL.createObjectURL(blob);
        if (type !== "video") cachePut(path, u); else revoked = u;
        setBlobUrl(u); setState("ok");
      } catch { setState("locked"); }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
    /* eslint-disable-next-line */
  }, [path, iv, enc, inView, playRequested, ready, status, retry]);

  const RetryBox = ({ big }) => (
    <button onClick={(e) => { e.stopPropagation(); setRetry((r) => r + 1); }} data-testid="media-retry-button" className="relative flex flex-col items-center gap-2 text-center px-4">
      <RefreshCw size={big ? 28 : 22} className="text-[#14A89A]" />
      <span className={`font-body ${big ? "text-sm" : "text-xs"} text-zinc-200`}>Could not load, tap to try again</span>
    </button>
  );

  // Blurred tiny thumbnail, instant paint placeholder behind loading/poster states.
  const ThumbBg = thumbUrl ? (
    <img src={thumbUrl} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110" data-testid="media-thumb-blur" />
  ) : null;

  // FULLSCREEN mode: decrypted media at full size, contained, no chrome (used inside the lightbox).
  if (fullscreen) {
    // If the bubble already decrypted this media, reuse its blob URL for an instant open (no spinner).
    const fsrc = preUrl || (enc ? blobUrl : mediaUrl(path));
    if (enc && !preUrl && state === "loading")
      return <Loader2 className="animate-spin text-zinc-400" size={32} />;
    if (enc && !preUrl && state === "error") return <RetryBox big />;
    if (enc && !preUrl && state === "locked")
      return (
        <div className="flex flex-col items-center gap-2 text-center px-4" data-testid="locked-media">
          <Lock size={30} className="text-[#14A89A]" strokeWidth={1.5} />
          <span className="font-body text-sm text-zinc-200">Encrypted - unlock to view</span>
        </div>
      );
    if (!fsrc) return null;
    return (
      <div className="relative inline-block max-w-full max-h-[88vh]" data-testid="lightbox-media-wrap">
        {type === "video"
          ? <video src={fsrc} className="max-w-full max-h-[88vh] block" controls controlsList="nodownload" disableRemotePlayback autoPlay playsInline onClick={(e) => e.stopPropagation()} data-testid="lightbox-media" />
          : (onZoomChange && !ov)
            ? <ZoomableImage src={fsrc} onZoomChange={onZoomChange} className="max-w-full max-h-[88vh] object-contain block" />
            : <img src={fsrc} alt="" className="max-w-full max-h-[88vh] object-contain block" onClick={(e) => e.stopPropagation()} data-testid="lightbox-media" />}
        <TextOverlay overlay={ov} />
      </div>
    );
  }

  // CONTAIN mode (feed): media fills column width (cover), tap to open full screen (contain, nothing cut off).
  if (contain) {
    const mediaCls = "block w-full h-[44vh] md:h-[55vh] object-cover";
    const frameCls = "relative w-full overflow-hidden border border-white/10 bg-black";

    // Video poster, decrypt + play only when tapped (prevents large-video memory crashes).
    if (type === "video" && !playRequested) {
      return (
        <div className={frameCls} ref={observe}>
          {ThumbBg}
          <button onClick={() => setPlayRequested(true)} data-testid="video-play-button" className={`relative w-full h-[44vh] md:h-[55vh] flex flex-col items-center justify-center gap-3 group ${thumbUrl ? "bg-black/40" : "bg-[#0d0d0d]"}`}>
            <div className="w-16 h-16 rounded-full bg-[#14A89A]/90 flex items-center justify-center group-hover:scale-105 transition-transform">
              <Play size={26} className="text-[#0A0A0A] ml-1" fill="#0A0A0A" />
            </div>
            <span className="font-body text-xs text-zinc-200 flex items-center gap-1.5">{enc && <Lock size={11} className="text-[#14A89A]" />} Tap to play video</span>
          </button>
        </div>
      );
    }

    if (enc && state === "loading")
      return (
        <div className={frameCls} ref={observe}>
          <div className={`w-full h-[44vh] md:h-[55vh] flex items-center justify-center relative ${thumbUrl ? "" : "bg-[#141414]"}`}>
            {ThumbBg}
            <Loader2 className="animate-spin text-zinc-300 relative" />
          </div>
        </div>
      );
    if (enc && state === "error")
      return (
        <div className={frameCls} ref={observe}>
          <div className="w-full h-[44vh] md:h-[55vh] flex items-center justify-center relative bg-[#141414]">{ThumbBg}<RetryBox /></div>
        </div>
      );
    if (enc && state === "locked")
      return (
        <div className={frameCls} ref={observe}>
          <div className="w-full h-[44vh] md:h-[55vh] flex flex-col items-center justify-center gap-2 text-center px-4 bg-[#141414]" data-testid="locked-media">
            <Lock size={26} className="text-[#14A89A]" strokeWidth={1.5} />
            <span className="font-body text-xs text-zinc-300">Encrypted - unlock to view</span>
          </div>
        </div>
      );

    const src = enc ? blobUrl : mediaUrl(path);
    if (!src) return null;

    return (
      <div className={frameCls} ref={observe}>
        {ThumbBg}
        {type === "video"
          ? <video ref={videoRef} src={src} className={`${mediaCls} relative z-10`} controls controlsList="nodownload" disableRemotePlayback autoPlay playsInline preload="auto" data-testid="post-media-video" />
          : <img src={src} alt="" loading="lazy" onLoad={() => setImgLoaded(true)} onClick={() => onExpand && onExpand()} className={`${mediaCls} cursor-pointer relative z-10 transition-opacity duration-500 ${imgLoaded ? "opacity-100" : "opacity-0"}`} data-testid="post-media-image" />}
        {type !== "video" && (
          <button onClick={() => onExpand && onExpand()} data-testid="media-expand-button" title="View full screen" className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full backdrop-blur-sm transition-colors">
            <Maximize2 size={16} />
          </button>
        )}
        <TextOverlay overlay={ov} />
      </div>
    );
  }

  // DEFAULT (cover) mode - used by the profile grid (square thumbnails).
  if (!enc) return <Media path={path} type={type} className={className} />;

  if (lazy && type === "video" && !playRequested)
    return (
      <div ref={observe} className={`relative bg-[#141414] overflow-hidden ${className}`} style={{ minHeight: 200 }}>
        {ThumbBg}
        <button onClick={() => setPlayRequested(true)} data-testid="video-play-button" className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/30 group">
          <div className="w-14 h-14 rounded-full bg-[#14A89A]/90 flex items-center justify-center group-hover:scale-105 transition-transform">
            <Play size={24} className="text-[#0A0A0A] ml-1" fill="#0A0A0A" />
          </div>
          <span className="font-body text-xs text-zinc-200 flex items-center gap-1.5"><Lock size={11} className="text-[#14A89A]" /> Tap to play video</span>
        </button>
      </div>
    );

  return (
    <div
      ref={lazy ? observe : undefined}
      className={`relative bg-[#141414] overflow-hidden ${className}`}
      style={{ minHeight: 140 }}
    >
      {state === "error" && <div className="absolute inset-0 flex items-center justify-center">{ThumbBg}<RetryBox /></div>}
      {state === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center">
          {ThumbBg}
          <Loader2 className="animate-spin text-zinc-300 relative" />
        </div>
      )}
      {state === "locked" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-4" data-testid="locked-media">
          <Lock size={26} className="text-[#14A89A]" strokeWidth={1.5} />
          <span className="font-body text-xs text-zinc-300">Encrypted - unlock to view</span>
        </div>
      )}
      {state === "ok" && (type === "video"
        ? <video ref={videoRef} src={blobUrl} style={imgStyle} className="absolute inset-0 w-full h-full object-cover" controls controlsList="nodownload" disableRemotePlayback playsInline data-testid="post-media-video" />
        : <img src={blobUrl} alt="" style={imgStyle} onClick={() => onExpand && onExpand(blobUrl)} className={`absolute inset-0 w-full h-full object-cover ${onExpand ? "cursor-pointer" : ""}`} data-testid="post-media-image" />)}
      {state === "ok" && type !== "video" && onExpand && (
        <button onClick={() => onExpand(blobUrl)} data-testid="media-expand-button" title="View full screen" className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full backdrop-blur-sm transition-colors z-10">
          <Maximize2 size={16} />
        </button>
      )}
      {state === "ok" && <TextOverlay overlay={ov} />}
    </div>
  );
};

// Renders one or many media slides as a carousel. Single slide = plain SecureMedia.
export const PostMedia = ({ media, enc, className, contain }) => {
  const [idx, setIdx] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const touchX = useRef(null);
  const slides = (media && media.length) ? media.filter((m) => m && m.path) : [];
  const single = slides.length <= 1;

  const goNext = () => { setZoomed(false); setIdx((i) => Math.min(slides.length - 1, i + 1)); };
  const goPrev = () => { setZoomed(false); setIdx((i) => Math.max(0, i - 1)); };
  const [zoomed, setZoomed] = useState(false);

  // Keyboard nav while the lightbox is open.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(false);
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    /* eslint-disable-next-line */
  }, [expanded, slides.length]);

  // Brief swipe hint when a multi-slide post is first opened full-screen.
  useEffect(() => {
    if (expanded && slides.length > 1) {
      setShowHint(true);
      const t = setTimeout(() => setShowHint(false), 2200);
      return () => clearTimeout(t);
    }
    setShowHint(false);
  }, [expanded, slides.length]);

  if (!slides.length) return null;
  const m = slides[Math.min(idx, slides.length - 1)];

  const onTouchStart = (e) => { touchX.current = e.touches.length === 1 && !zoomed ? e.touches[0].clientX : null; };
  const onTouchMove = (e) => { if (e.touches.length > 1) touchX.current = null; };
  const onTouchEnd = (e) => {
    if (touchX.current == null || zoomed || e.touches.length) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx < -45) goNext();
    else if (dx > 45) goPrev();
    touchX.current = null;
  };
  const near = [idx - 1, idx + 1].filter((n) => n >= 0 && n < slides.length && slides[n].type !== "video");

  return (
    <div className="relative" data-testid="post-media-carousel">
      <SecureMedia key={`${m.path}-${idx}`} path={m.path} iv={m.iv} enc={enc} type={m.type} className={className} contain={contain} onExpand={() => setExpanded(true)} thumb={m.thumb} thumbIv={m.thumb_iv} overlay={m.overlay} overlayCt={m.overlay_ct} overlayIv={m.overlay_iv} />
      {!single && (
        <>
          {idx > 0 && (
            <button onClick={() => goPrev()} data-testid="carousel-prev" className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full backdrop-blur-sm transition-colors z-10">
              <ChevronLeft size={18} />
            </button>
          )}
          {idx < slides.length - 1 && (
            <button onClick={() => goNext()} data-testid="carousel-next" className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full backdrop-blur-sm transition-colors z-10">
              <ChevronRight size={18} />
            </button>
          )}
          <div className="absolute top-2 left-2 bg-black/55 text-white text-[11px] font-body px-2 py-0.5 rounded-full backdrop-blur-sm z-10">{idx + 1}/{slides.length}</div>
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} data-testid={`carousel-dot-${i}`} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`} />
            ))}
          </div>
        </>
      )}

      {expanded && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" data-testid="media-lightbox" style={{ touchAction: "none" }} onClick={() => { setZoomed(false); setExpanded(false); }} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
          <button onClick={(e) => { e.stopPropagation(); setZoomed(false); setExpanded(false); }} data-testid="lightbox-close" className="absolute top-4 right-4 z-20 text-white/80 hover:text-white bg-black/40 rounded-full p-2 transition-colors">
            <X size={24} />
          </button>
          <SecureMedia key={`fs-${m.path}-${idx}`} path={m.path} iv={m.iv} enc={enc} type={m.type} fullscreen onZoomChange={setZoomed} thumb={m.thumb} thumbIv={m.thumb_iv} overlay={m.overlay} overlayCt={m.overlay_ct} overlayIv={m.overlay_iv} />
          <div className="hidden" aria-hidden="true">{near.map((n) => <SecureMedia key={`pre-${slides[n].path}`} path={slides[n].path} iv={slides[n].iv} enc={enc} type={slides[n].type} fullscreen />)}</div>
          {showHint && !single && (
            <div className="absolute inset-0 flex items-center justify-between px-6 pointer-events-none z-30" data-testid="swipe-hint">
              <ChevronLeft size={42} className="text-white swipe-hint-left drop-shadow-lg" />
              <ChevronRight size={42} className="text-white swipe-hint-right drop-shadow-lg" />
            </div>
          )}
          {!single && (
            <>
              <div className="absolute top-5 left-5 bg-black/55 text-white text-xs font-body px-2.5 py-1 rounded-full backdrop-blur-sm z-20">{idx + 1}/{slides.length}</div>
              <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 z-20">
                {slides.map((_, i) => (
                  <button key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }} data-testid={`lightbox-dot-${i}`} className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const Crest = ({ className = "h-9 w-9" }) => (
  <img src="/logo-crest.png" alt="Sovereign Signal" className={className} />
);

// Shared across all Avatar instances so a given encrypted portrait is fetched
// and decrypted once, then reused everywhere it appears.
const _avatarBlobCache = new Map(); // path -> blobUrl | Promise<blobUrl|null>

export const Avatar = ({ name, src, iv, size = 40 }) => {
  const cc = useCrypto();
  const decryptNetBytes = cc?.decryptNetBytes;
  const netReady = cc?.netReady;
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const [url, setUrl] = useState(() => (src && src.startsWith("http")) ? src : null);

  useEffect(() => {
    let cancelled = false;
    if (!src) { setUrl(null); return; }
    if (src.startsWith("http")) { setUrl(src); return; }
    const encIv = iv || getAvatarIv(src);
    if (!encIv) { setUrl(mediaUrl(src)); return; } // legacy plaintext portrait
    if (!netReady || !decryptNetBytes) { return; } // wait for the network key
    if (_avatarBlobCache.has(src)) {
      const v = _avatarBlobCache.get(src);
      if (typeof v === "string") { setUrl(v); return; }
      v.then((u) => { if (!cancelled && u) setUrl(u); });
      return;
    }
    const p = (async () => {
      try {
        const res = await axios.get(`${API}/files/${src}`, { responseType: "arraybuffer" });
        const plain = await decryptNetBytes(res.data, encIv);
        if (!plain) return null;
        const u = URL.createObjectURL(new Blob([plain], { type: "image/jpeg" }));
        _avatarBlobCache.set(src, u);
        return u;
      } catch { _avatarBlobCache.delete(src); return null; }
    })();
    _avatarBlobCache.set(src, p);
    p.then((u) => { if (!cancelled && u) setUrl(u); });
    return () => { cancelled = true; };
  }, [src, iv, netReady, decryptNetBytes]);

  return (
    <div
      className="flex items-center justify-center bg-[#1a1a1a] border border-white/10 text-[#14A89A] font-heading shrink-0 overflow-hidden rounded-full"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : initials}
    </div>
  );
};
